/**
 * SizeGuide.jsx — Luvia Jewelry Size Guide
 *
 * Jewelry-specific sizing guidance for:
 * - Rings (US sizes, diameter, circumference)
 * - Chains / Necklaces (length guide)
 * - Bracelets & Bangles (length + inner diameter)
 *
 * No apparel sizing (XS–XXL, chest/shoulder/length).
 */

import { useState, useCallback, useId } from 'react';
import { Link }   from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Layout     from '../../components/common/Layout/Layout';
import { BRAND }  from '../../config/brand';
import './SizeGuide.css';

/* ── Constants ──────────────────────────────────────────────────────────── */
const SITE_URL = BRAND?.url || process.env.REACT_APP_SITE_URL || 'https://theluviajewels.com';
const BRAND_NAME = BRAND?.name || 'Luvia';
const SIZE_GUIDE_URL = `${SITE_URL}/size-guide`;
const OG_IMAGE = `${SITE_URL}/og-image.jpg`;

/* ── Ring size chart (US sizes) ─────────────────────────────────────────── */
const RING_SIZES = [
  { us: '4',  diameter: '14.9', circumference: '46.8' },
  { us: '5',  diameter: '15.7', circumference: '49.3' },
  { us: '6',  diameter: '16.5', circumference: '51.9' },
  { us: '7',  diameter: '17.3', circumference: '54.4' },
  { us: '8',  diameter: '18.1', circumference: '57.0' },
  { us: '9',  diameter: '18.9', circumference: '59.5' },
  { us: '10', diameter: '19.8', circumference: '62.1' },
  { us: '11', diameter: '20.6', circumference: '64.6' },
  { us: '12', diameter: '21.4', circumference: '67.2' },
];

/* ── Chain / necklace length guide ──────────────────────────────────────── */
const CHAIN_LENGTHS = [
  { name: 'Choker', length: '14–16"', cm: '35–41', tip: 'Sits close to the neck.' },
  { name: 'Princess', length: '17–19"', cm: '43–48', tip: 'Hangs just below collarbone. Most versatile everyday length.' },
  { name: 'Matinee', length: '20–24"', cm: '51–61', tip: 'Mid-chest. Good for layering.' },
  { name: 'Opera', length: '26–36"', cm: '66–91', tip: 'Below bust. Can be doubled for layered styling.' },
  { name: 'Rope', length: '36–42"+', cm: '91–107+', tip: 'Long statement length. Wear doubled or tripled.' },
];

/* ── Bracelet chain lengths ─────────────────────────────────────────────── */
const BRACELET_LENGTHS = [
  { size: 'XS', length: '6.0"', cm: '15.2', fit: 'Very snug' },
  { size: 'S',  length: '6.5"', cm: '16.5', fit: 'Snug fit' },
  { size: 'M',  length: '7.0"', cm: '17.8', fit: 'Standard fit' },
  { size: 'L',  length: '7.5"', cm: '19.1', fit: 'Comfort fit' },
  { size: 'XL', length: '8.0"', cm: '20.3', fit: 'Relaxed fit' },
];

/* ── Indian bangle sizing (inner diameter) ─────────────────────────────── */
const BANGLE_SIZES = [
  { size: '2-2', inches: '2.125"', mm: '54', fit: 'Slim hand fit' },
  { size: '2-4', inches: '2.25"', mm: '57', fit: 'Common fit' },
  { size: '2-6', inches: '2.375"', mm: '60', fit: 'Most common fit' },
  { size: '2-8', inches: '2.5"', mm: '63', fit: 'Comfort fit' },
  { size: '2-10', inches: '2.625"', mm: '67', fit: 'Wider hand fit' },
  { size: '2-12', inches: '2.75"', mm: '70', fit: 'Relaxed fit' },
];

/* ── FAQ for jewelry sizing ─────────────────────────────────────────────── */
const FAQ_ITEMS = [
  {
    id: 'faq-ring',
    q: 'How do I measure my ring size at home?',
    a: 'Wrap a strip of paper or string around the base of your finger. Mark where it meets, measure the length in mm, and match to the circumference column in our ring chart. Or use our printable ring sizer if available.',
  },
  {
    id: 'faq-between',
    q: "What if I'm between ring sizes?",
    a: 'Size up for a comfortable fit. Rings that are slightly loose are easier to remove; snug rings can feel restrictive. For knuckle considerations, size up if your knuckle is wider than your base.',
  },
  {
    id: 'faq-chain',
    q: 'How do I choose necklace length?',
    a: 'Consider neckline and occasion. Chokers sit close to the neck; princess length works with most necklines. Measure a necklace you already own from clasp to end, or use a soft tape around your neck at the desired drop.',
  },
  {
    id: 'faq-bangle',
    q: 'How do I measure for a bracelet or bangle?',
    a: 'For chain bracelets, measure wrist circumference and add 0.5-1 inch based on preferred fit. For closed bangles, measure around the widest part of your hand and match to inner diameter size.',
  },
  {
    id: 'faq-wrongsize',
    q: "What if the size doesn't fit?",
    a: 'Returns and exchanges are currently unavailable. Please confirm sizing before ordering and contact our team on WhatsApp for fit help before purchase.',
    hasLink: false,
  },
];

/* ── Structured data ─────────────────────────────────────────────────────── */
const PAGE_SCHEMA = JSON.stringify({
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type':       'WebPage',
      '@id':         `${SIZE_GUIDE_URL}#webpage`,
      url:           SIZE_GUIDE_URL,
      name:          `Jewelry Size Guide | Rings, Chains & Bangles | ${BRAND_NAME}`,
      description:   `Ring, necklace, bracelet, and bangle sizing guide for ${BRAND_NAME}. Includes free shipping above 500 and current no-return policy guidance.`,
      inLanguage:    'en-IN',
      isPartOf:      { '@id': `${SITE_URL}/#website` },
      breadcrumb:    { '@id': `${SIZE_GUIDE_URL}#breadcrumb` },
    },
    {
      '@type': 'BreadcrumbList',
      '@id':   `${SIZE_GUIDE_URL}#breadcrumb`,
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
        { '@type': 'ListItem', position: 2, name: 'Size Guide', item: SIZE_GUIDE_URL },
      ],
    },
    {
      '@type':      'FAQPage',
      '@id':        `${SIZE_GUIDE_URL}#faq`,
      mainEntity:   FAQ_ITEMS.filter(f => !f.hasLink).map(f => ({
        '@type': 'Question',
        name:    f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
  ],
});

/* ── Inline SVG icons ────────────────────────────────────────────────────── */
function IconRuler() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true" focusable="false">
      <path d="M21.3 8.7l-9-9a1 1 0 0 0-1.4 0l-9 9a1 1 0 0 0 0 1.4l9 9a1 1 0 0 0 1.4 0l9-9a1 1 0 0 0 0-1.4z"/>
      <line x1="9" y1="15" x2="15" y2="9"/>
    </svg>
  );
}

function IconAlert() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true" focusable="false">
      <circle cx="12" cy="12" r="10"/>
      <line x1="12" y1="8" x2="12" y2="12"/>
      <line x1="12" y1="16" x2="12.01" y2="16"/>
    </svg>
  );
}

function IconArrowRight() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true" focusable="false">
      <line x1="5" y1="12" x2="19" y2="12"/>
      <polyline points="12 5 19 12 12 19"/>
    </svg>
  );
}

/* ── Accordion item ──────────────────────────────────────────────────────── */
function FaqItem({ item, isOpen, onToggle }) {
  const answerId   = useId();
  const questionId = useId();

  return (
    <div className={`sg-faq__item${isOpen ? ' sg-faq__item--open' : ''}`}>
      <button
        type="button"
        id={questionId}
        className="sg-faq__q"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={answerId}
      >
        <span>{item.q}</span>
        <span className="sg-faq__chevron" aria-hidden="true">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2.5"
            strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </span>
      </button>
      <div
        id={answerId}
        role="region"
        aria-labelledby={questionId}
        className="sg-faq__body"
        hidden={!isOpen}
      >
        <div className="sg-faq__a">
          <p>{item.a}</p>
        </div>
      </div>
    </div>
  );
}

/* ── Main Component ─────────────────────────────────────────────────────── */
export default function SizeGuide() {
  const [openFaqs, setOpenFaqs] = useState(new Set());

  const toggleFaq = useCallback((id) => {
    setOpenFaqs(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const pageTitle = `Jewelry Size Guide | Rings, Chains & Bangles | ${BRAND_NAME}`;
  const pageDesc  = `Find your perfect jewelry fit with ring, necklace, bracelet, and bangle size charts. Free shipping above 500. Returns are currently unavailable.`;

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large" />
        <link rel="canonical" href={SIZE_GUIDE_URL} />
        <link rel="prefetch" href="/shop/women" as="document" />

        <meta property="og:type" content="website" />
        <meta property="og:url" content={SIZE_GUIDE_URL} />
        <meta property="og:site_name" content={BRAND_NAME} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Jewelry Size Guide — Luvia" />
        <meta property="og:locale" content="en_IN" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDesc} />
        <meta name="twitter:image" content={OG_IMAGE} />
        <meta name="twitter:image:alt" content="Jewelry Size Guide — Luvia" />

        <script type="application/ld+json">{PAGE_SCHEMA}</script>
      </Helmet>

      <Layout showFooter showBottomTab>
        <main className="sg-page" id="main-content">

          <a href="#sg-main" className="sg-skip">Skip to ring size chart</a>

          {/* Hero band */}
          <div className="sg-hero">
            <div className="sg-hero__inner">
              <nav className="sg-breadcrumb" aria-label="Breadcrumb">
                <ol className="sg-breadcrumb__list">
                  <li>
                    <Link to="/" className="sg-breadcrumb__link">Home</Link>
                  </li>
                  <li aria-hidden="true" className="sg-breadcrumb__sep">
                    <span>›</span>
                  </li>
                  <li>
                    <span aria-current="page" className="sg-breadcrumb__current">
                      Size Guide
                    </span>
                  </li>
                </ol>
              </nav>

              <h1 className="sg-hero__title">Jewelry Size Guide</h1>
              <p className="sg-hero__subtitle">
                Ring, necklace, bracelet, and bangle measurements so you can choose with confidence.
              </p>
            </div>
          </div>

          {/* Body layout */}
          <div className="sg-layout" id="sg-main">

            <aside className="sg-sidebar" aria-label="Quick tips">
              <div className="sg-sidebar__inner">
                <p className="sg-sidebar__label">
                  <IconRuler />
                  Quick Tips
                </p>
                <ol className="sg-sidebar__steps" aria-label="Quick tips">
                  <li className="sg-sidebar__step">
                    <div>
                      <p className="sg-sidebar__step-text">Measure ring size in the evening — fingers swell slightly during the day.</p>
                    </div>
                  </li>
                  <li className="sg-sidebar__step">
                    <div>
                      <p className="sg-sidebar__step-text">For chains, measure a necklace you love and compare to our length guide.</p>
                    </div>
                  </li>
                  <li className="sg-sidebar__step">
                    <div>
                      <p className="sg-sidebar__step-text">Bangles: measure over the widest part of your hand if it must slip over the wrist.</p>
                    </div>
                  </li>
                </ol>
                <div className="sg-sidebar__divider" />
                <Link to="/contact" className="sg-sidebar__link">
                  Need sizing help? Contact us →
                </Link>
              </div>
            </aside>

            <div className="sg-main">

              {/* Ring sizes */}
              <section className="sg-section" id="sg-chart-panel" aria-labelledby="ring-h">
                <h2 id="ring-h" className="sg-section__title">Ring Sizes</h2>
                <p className="sg-section__note">
                  US standard sizes. Diameter and circumference in mm (based on common jewelry standards).
                </p>
                <div className="sg-table-wrap" tabIndex={0} aria-label="Ring size chart — scroll horizontally to see all columns">
                  <table className="sg-table" aria-label="Ring size chart">
                    <caption className="sg-sr-only">
                      Ring sizes US 4–12 with diameter and circumference in mm
                    </caption>
                    <thead>
                      <tr>
                        <th scope="col">US Size</th>
                        <th scope="col">Diameter (mm)</th>
                        <th scope="col">Circumference (mm)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {RING_SIZES.map((row) => (
                        <tr key={row.us}>
                          <td><strong>{row.us}</strong></td>
                          <td>{row.diameter}</td>
                          <td>{row.circumference}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="sg-tip" role="note">
                  <IconAlert />
                  <span>Between sizes? Size up for comfort.</span>
                </div>
                <div className="sg-chart-cta">
                  <Link to="/shop/women/rings" className="sg-btn sg-btn--gold-sm" aria-label="Shop rings">
                    Shop Rings
                    <IconArrowRight />
                  </Link>
                </div>
              </section>

              {/* Chain / necklace lengths */}
              <section className="sg-section" aria-labelledby="chain-h">
                <h2 id="chain-h" className="sg-section__title">Chain & Necklace Lengths</h2>
                <p className="sg-section__note">
                  Standard lengths and where they typically sit.
                </p>
                <div className="sg-table-wrap" tabIndex={0} aria-label="Chain length guide">
                  <table className="sg-table" aria-label="Necklace length guide">
                    <thead>
                      <tr>
                        <th scope="col">Style</th>
                        <th scope="col">Length (in)</th>
                        <th scope="col">Length (cm)</th>
                        <th scope="col">Where it sits</th>
                      </tr>
                    </thead>
                    <tbody>
                      {CHAIN_LENGTHS.map((row) => (
                        <tr key={row.name}>
                          <td><strong>{row.name}</strong></td>
                          <td>{row.length}</td>
                          <td>{row.cm}</td>
                          <td>{row.tip}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="sg-chart-cta">
                  <Link to="/shop/women/necklaces" className="sg-btn sg-btn--gold-sm" aria-label="Shop necklaces">
                    Shop Necklaces
                    <IconArrowRight />
                  </Link>
                </div>
              </section>

              {/* Bracelet and bangle sizes */}
              <section className="sg-section" aria-labelledby="bangle-h">
                <h2 id="bangle-h" className="sg-section__title">Bracelet &amp; Bangle Sizing</h2>
                <p className="sg-section__note">
                  Use bracelet length for chain styles and inner diameter for rigid bangles.
                </p>
                <div className="sg-table-wrap" tabIndex={0} aria-label="Bracelet chain size guide">
                  <table className="sg-table" aria-label="Bracelet size chart">
                    <thead>
                      <tr>
                        <th scope="col">Bracelet Size</th>
                        <th scope="col">Length (in)</th>
                        <th scope="col">Length (cm)</th>
                        <th scope="col">Fit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {BRACELET_LENGTHS.map((row) => (
                        <tr key={row.size}>
                          <td><strong>{row.size}</strong></td>
                          <td>{row.length}</td>
                          <td>{row.cm}</td>
                          <td>{row.fit}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="sg-table-wrap" tabIndex={0} aria-label="Bangle size guide">
                  <table className="sg-table" aria-label="Bangle size chart">
                    <thead>
                      <tr>
                        <th scope="col">Bangle Size</th>
                        <th scope="col">Inner Diameter (in)</th>
                        <th scope="col">Inner Diameter (mm)</th>
                        <th scope="col">Fit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {BANGLE_SIZES.map((row) => (
                        <tr key={row.size}>
                          <td><strong>{row.size}</strong></td>
                          <td>{row.inches}</td>
                          <td>{row.mm}</td>
                          <td>{row.fit}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="sg-chart-cta">
                  <Link to="/shop/women/bangles" className="sg-btn sg-btn--gold-sm" aria-label="Shop bangles">
                    Shop Bangles
                    <IconArrowRight />
                  </Link>
                </div>
              </section>

              {/* FAQ */}
              <section className="sg-section" aria-labelledby="faq-h">
                <h2 id="faq-h" className="sg-section__title">Common Questions</h2>
                <div className="sg-faq">
                  {FAQ_ITEMS.map((item) => (
                    <FaqItem
                      key={item.id}
                      item={item}
                      isOpen={openFaqs.has(item.id)}
                      onToggle={() => toggleFaq(item.id)}
                    />
                  ))}
                </div>
              </section>

            </div>
          </div>

          {/* CTA band */}
          <div className="sg-cta-band">
            <div className="sg-cta-band__inner">
              <div className="sg-cta-band__text">
                <p className="sg-cta-band__eyebrow">Ready to shop</p>
                <h2 className="sg-cta-band__title">Explore Our Collection</h2>
                <p className="sg-cta-band__sub">Free shipping on orders above 500 | Returns currently unavailable</p>
              </div>
              <div className="sg-cta-band__actions">
                <Link to="/shop" className="sg-btn sg-btn--primary">
                  Shop
                  <IconArrowRight />
                </Link>
                <Link to="/shop/women" className="sg-btn sg-btn--ghost">
                  Shop Women
                  <IconArrowRight />
                </Link>
              </div>
            </div>
          </div>

        </main>
      </Layout>
    </>
  );
}
